import { useMutation } from '@apollo/client/react';
import { UPDATE_AUTOGIRO_MUTATION } from '../../integrations/allkort/graphql/gql/CustomerMutations';
import { GET_AUTOGIRO_DATA_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import {
    UpdateAutogiroInput,
    UpdateAutogiroResponse,
} from '../../integrations/allkort/graphql/interface/CustomerMutation';

/**
 * useUpdateAutogiro
 *
 * Custom hook for connecting or updating the customer's autogiro (direct debit).
 *
 * @example
 * ```tsx
 * const { updateAutogiro, loading, error } = useUpdateAutogiro();
 *
 * await updateAutogiro({
 *   customerId: 226617,
 *   requestedClearingNumber: '1234',
 *   requestedAccountNumber: '12345678',
 * });
 * ```
 */
export const useUpdateAutogiro = () => {
	const [updateAutogiroMutation, { loading, error, data }] = useMutation<
		UpdateAutogiroResponse,
		UpdateAutogiroInput
	>(UPDATE_AUTOGIRO_MUTATION, {
		errorPolicy: 'all',
		refetchQueries: [{ query: GET_AUTOGIRO_DATA_QUERY }],
		awaitRefetchQueries: true,
	});

	const updateAutogiro = async (input: UpdateAutogiroInput) => {
		return updateAutogiroMutation({
			variables: {
				approved: input.approved ?? null,
				customerId: input.customerId,
				requestedAccountNumber: input.requestedAccountNumber ?? null,
				requestedClearingNumber: input.requestedClearingNumber ?? null,
			},
		});
	};

	return { updateAutogiro, loading, error, data };
};
